import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, CheckSquare, X, Trash2, ExternalLink } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';

interface QuickCapture {
  id: string;
  text: string;
  status: string;
  triaged_to_task_id: string | null;
  triaged_to_meeting_id: string | null;
  created_at: string;
  updated_at: string;
}

interface TriageResponse {
  capture: QuickCapture;
  task: { id: string; title: string };
}

type FilterTab = 'all' | 'inbox' | 'triaged' | 'dismissed';

export function InboxPage() {
  const [captures, setCaptures] = useState<QuickCapture[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>('inbox');

  async function load() {
    try {
      const params = activeTab !== 'all' ? `?status=${activeTab}` : '';
      setCaptures(await apiGet<QuickCapture[]>(`/quick-captures${params}`));
    } catch (err) {
      console.error('Failed to load captures:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    load();
  }, [activeTab]);

  async function handleCreateTask(capture: QuickCapture) {
    try {
      await apiPost<TriageResponse>(`/quick-captures/${capture.id}/triage-task`, {
        title: capture.text,
      });
      load();
    } catch (err) {
      console.error('Failed to triage capture:', err);
    }
  }

  async function handleDismiss(id: string) {
    try {
      await apiPost(`/quick-captures/${id}/dismiss`, {});
      load();
    } catch (err) {
      console.error('Failed to dismiss capture:', err);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this capture permanently?')) return;
    try {
      await apiDelete(`/quick-captures/${id}`);
      load();
    } catch (err) {
      console.error('Failed to delete capture:', err);
    }
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'inbox', label: 'Inbox' },
    { key: 'triaged', label: 'Triaged' },
    { key: 'dismissed', label: 'Dismissed' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Inbox</h1>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-bg-input rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-bg-surface text-text-primary font-medium shadow-sm'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Capture List */}
      {captures.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Inbox size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">
            {activeTab === 'inbox'
              ? 'Inbox is empty'
              : activeTab === 'triaged'
              ? 'No triaged captures'
              : activeTab === 'dismissed'
              ? 'No dismissed captures'
              : 'No captures yet'}
          </p>
          <p className="text-sm">
            Use the Quick Capture button in the header to jot down thoughts quickly.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {captures.map(capture => (
            <div
              key={capture.id}
              className={`px-4 py-3 bg-bg-card border border-border rounded-lg group transition-colors ${
                capture.status === 'dismissed' ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm ${
                      capture.status === 'dismissed'
                        ? 'line-through text-text-muted'
                        : 'text-text-primary'
                    }`}
                  >
                    {capture.text}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs text-text-muted">
                      {formatRelativeTime(capture.created_at)}
                    </span>
                    <StatusBadge status={capture.status} />
                    {capture.triaged_to_task_id && (
                      <Link
                        to={`/tasks/${capture.triaged_to_task_id}`}
                        className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors"
                      >
                        <ExternalLink size={10} />
                        View task
                      </Link>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {capture.status === 'inbox' && (
                    <>
                      <button
                        onClick={() => handleCreateTask(capture)}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                        title="Create task from this capture"
                      >
                        <CheckSquare size={12} />
                        Create Task
                      </button>
                      <button
                        onClick={() => handleDismiss(capture.id)}
                        className="p-1 text-text-muted hover:text-warning transition-colors"
                        title="Dismiss"
                      >
                        <X size={14} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(capture.id)}
                    className="p-1 text-text-muted hover:text-danger transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    inbox: 'bg-accent/10 text-accent',
    triaged: 'bg-success/10 text-success',
    dismissed: 'bg-text-muted/10 text-text-muted',
  };

  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[status] ?? ''}`}>
      {status}
    </span>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
